import { Button, buttonStyles } from "@components/ui/Button"
import { alerts } from "@features/alerts/alerts"
import { useFerve } from "@features/ferve/FerveProvider"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"
import { tv } from "@utils/tw"
import { useRef, useState } from "react"

export type Props = {
  ferveHash: string
  amounts: (number | null)[]
  buttonLabel?: string
  altDonationLink?: {
    title: string
    url: string
    target: string
  }
  onAddToCart?: (amount: number, isOther?: boolean) => void
}

const DEFAULT_AMOUNTS = [25, 100, 250, 500]

export function DonationSelector(props: Props) {
  const ref = useRef<HTMLFormElement>(null)
  const ferve = useFerve()
  const flyingCarpet = useFlyingCarpet()
  const [selectedAmount, setSelectedAmount] = useState<number | "other" | null>(null)
  const [otherAmount, setOtherAmount] = useState<string>("")
  const styles = createStyles({
    hasAltLink: !!props.altDonationLink,
  })

  const amounts = DEFAULT_AMOUNTS.map((amount, i) => props.amounts[i] ?? amount)

  const [working, setWorking] = useState(false)

  const donate = async () => {
    setWorking(true)
    const total = selectedAmount === "other" ? parseFloat(otherAmount.replace(/[^0-9.]/g, "")) : selectedAmount
    if (!total || typeof total !== "number") {
      alerts.displayAlert({ title: "Oops...", description: "Please select a donation amount" })
      setWorking(false)
      return
    }
    const pricing = await ferve.cart.getPricing(props.ferveHash)
    const price = pricing.find((p) => p.price === -1 || p.label === "Other")
    if (!price) {
      alerts.displayAlert({ title: "Oops...", description: "Donation pricing not found" })
      setWorking(false)
      return
    }
    try {
      await ferve.cart.addItemsToCart(props.ferveHash, [
        {
          quantity: total * 100,
          type: {
            priceTypeHash: price.priceTypeHash,
          },
        },
      ])
      props.onAddToCart?.(total, selectedAmount === "other")
    } catch (err) {
      alerts.displayAlert({ title: "Oops...", description: (err as Error)?.message })
      setWorking(false)
      return
    }
    setWorking(false)
    flyingCarpet.launch("cart")
  }

  return (
    <form
      ref={ref}
      className={styles.base()}
      onSubmit={(e) => {
        e.preventDefault()
        donate()
      }}
    >
      <div className={styles.amountRow()}>
        {amounts.map((amount, i) => (
          <span key={i} className={styles.amountButton()}>
            <button
              type="button"
              className={amountButton({ selected: selectedAmount === amount })}
              onClick={() => setSelectedAmount(amount)}
            >
              ${amount}
            </button>
          </span>
        ))}
        <span className={styles.amountButton()}>
          <input
            className={amountButton({ selected: selectedAmount === "other" })}
            placeholder="Other"
            onClick={() => {
              if (selectedAmount !== "other") {
                setSelectedAmount("other")
              }
            }}
            value={selectedAmount === "other" ? otherAmount : ""}
            onChange={(e) => {
              setSelectedAmount("other")
              let value = e.target.value.replace(/[^0-9.]/g, "")
              if (value === "") {
                setOtherAmount("")
              } else {
                setOtherAmount("$" + value)
              }
            }}
          />
        </span>
      </div>
      <div className={styles.submitRow()}>
        {props.altDonationLink && (
          <Button href={props.altDonationLink.url} shape="branded" variant="inverted">
            {props.altDonationLink.title}
          </Button>
        )}
        <Button type="submit" shape="branded" variant="primary" loading={working}>
          {props.buttonLabel ?? "Donate"}
        </Button>
      </div>
    </form>
  )
}

const createStyles = tv({
  base: "flex flex-col gap-3 md:gap-3 w-full",
  slots: {
    amountRow: "flex gap-3 lg:gap-2 xl:gap-2",
    submitRow: "flex gap-3 lg:gap-2 xl:gap-2 md:justify-end",
    amountButton: "flex-1 [&_input]:w-full [&_input]:text-center [&>*]:w-full gap-1 md:gap-2",
  },
  variants: {
    hasAltLink: {
      true: {
        submitRow: "flex-col sm:flex-row [&>*]:flex-1 [&>*]:basis-auto",
      },
      false: {
        submitRow: "flex-col sm:justify-end",
      },
    },
  },
})

const amountButton = tv({
  extend: buttonStyles,
  base: [
    "!px-1 w-full flex-1 !ring-transparent !ring-offset-transparent",
    "!bg-white !text-black placeholder:text-black hover:bg-bg-low [.is-gift-card_&]:hover:!bg-white/90",
  ],
  variants: {
    selected: {
      true: "!bg-white/90 shadow-[0_0_0_2px_black] [.is-gift-card_&]:!bg-orange [.is-gift-card_&]:hover:!bg-orange",
      false: "",
    },
  },
  defaultVariants: {
    shape: "branded",
    variant: "inverted",
  },
})
